import * as React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { AlertEvaluationValue } from '@quality24/inpatient-typings';

import { Button, RangeInputField, TextArea } from '@quality24/design-system';
import SmileyInputField from '@/inpatient-patient-pwa/molecules/SmileyInputField';

export interface Props {
  onSubmit?: (feedback: Partial<AlertEvaluationValue>) => void;
  onRefuse?: () => void;
  message?: string;
  title?: string;
  evalType?: '_5_POINT_SMILEY' | '10_POINT' | string;
}

const getValidationSchema = (t: TFunction) =>
  Yup.object().shape({
    score: Yup.number().required(t('validation.required')),
    text: Yup.string(),
  });

const CallEvaluationForm: React.FunctionComponent<Props> = ({
  onSubmit = () => {},
  onRefuse = () => {},
  evalType = '_10_POINT',
  title,
  message,
}) => {
  const { t } = useTranslation();
  const resolvedTitle = title ?? t('callEvaluation.title');
  const resolvedMessage = message ?? t('callEvaluation.message');
  const validationSchema = React.useMemo(() => getValidationSchema(t), [t]);
  const formik = useFormik<Partial<AlertEvaluationValue>>({
    initialValues: { score: 0, text: '' },
    validationSchema,
    validateOnMount: true,
    onSubmit: (values) => onSubmit(values),
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <h2 className="text-primary text-center mb-4">{resolvedTitle}</h2>
      <p className="page-description mb-5" style={{ whiteSpace: 'pre-wrap' }}>
        {resolvedMessage}
      </p>

      {evalType === '_5_POINT_SMILEY' ? (
        <SmileyInputField
          className="mb-5"
          value={formik.values.score}
          onChange={(score) => formik.setFieldValue('score', score)}
        />
      ) : (
        <RangeInputField
          className="mb-5"
          value={formik.values.score}
          errorText={formik.errors.score}
          onChange={(score) => formik.setFieldValue('score', score)}
          min={1}
        />
      )}

      <TextArea
        id="formFeedback"
        label={t('callEvaluation.comment')}
        name="text"
        value={formik.values.text}
        errorText={formik.errors.text}
        onChange={formik.handleChange}
      />

      <div className="d-flex justify-content-end mt-4">
        <Button className="m-2" variant="outline" onClick={onRefuse}>
          {t('callEvaluation.skip')}
        </Button>
        <Button className="m-2" type="submit" disabled={!formik.isValid}>
          {t('common.send')}
        </Button>
      </div>
    </form>
  );
};

export default CallEvaluationForm;
